import { useEffect, useState } from "react";
import { Select, DatePicker, Button, message } from "antd";
import axios from "axios";
import type { DatePickerProps } from "antd";
import { useTranslation } from "react-i18next";

// Take the value type from antd itself so it matches whichever moment typings antd resolves.
type PickerValue = Parameters<NonNullable<DatePickerProps["onChange"]>>[0];

const ChangeOwnership: React.FC = () => {
  const [apartments, setApartments] = useState<string[]>([]);
  const [parkings, setParkings] = useState<string[]>([]);
  const [owners, setOwners] = useState<any[]>([]);
  const [selectedApartments, setSelectedApartments] = useState<string[]>([]);
  const [selectedParkings, setSelectedParkings] = useState<string[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [changeFrom, setChangeFrom] = useState<PickerValue>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [t] = useTranslation("common");

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchApartments(),
          fetchParkings(),
          fetchOwners(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const fetchApartments = async () => {
    try {
      const res = await axios
        .get("/apartments/all-rooms")
        .then((res) => res.data);
      setApartments(res);
    } catch (err) {
      console.log(err);
      message.error(t("Something went wrong. Please try again later."));
    }
  };

  const fetchParkings = async () => {
    try {
      const res = await axios
        .get("/parkings/list", { params: { search: "", ownerId: "" } })
        .then((res) => res.data);
      const parkingNames = res.map((parking: any) => parking.ParkingName);
      setParkings(parkingNames);
    } catch (err) {
      console.log(err);
      message.error(t("Something went wrong. Please try again later."));
    }
  };

  const fetchOwners = async () => {
    try {
      const res = await axios
        .get("/users/list", { params: { search: "" } })
        .then((res) => res.data);
      setOwners(res);
    } catch (err) {
      console.log(err);
      message.error(t("Something went wrong. Please try again later."));
    }
  };

  const handleUpdate = async () => {
    if (!selectedOwner) {
      message.warning(t("dashboard.ChangeOwnership.Please select an owner"));
      return;
    }

    if (!changeFrom) {
      message.warning(t("dashboard.ChangeOwnership.Please select a date"));
      return;
    }

    if (selectedApartments.length === 0 && selectedParkings.length === 0) {
      message.warning(
        t("dashboard.ChangeOwnership.Please select at least one apartment or parking")
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios
        .post("/dashboard/change-ownership", {
          apartments: selectedApartments,
          parkings: selectedParkings,
          ownerId: selectedOwner,
          changeFrom: changeFrom.format("YYYY-MM-DD"),
        })
        .then((res) => res.data);

      if (res.success) {
        message.success(t("dashboard.ChangeOwnership.Ownership changed successfully"));
        // Reset form
        setSelectedApartments([]);
        setSelectedParkings([]);
        setSelectedOwner(null);
        setChangeFrom(null);
      }
    } catch (err: any) {
      console.log(err);
      message.error(
        err.response?.data?.message ||
          t("Something went wrong. Please try again later.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block mb-2 font-semibold">
            {t("dashboard.ChangeOwnership.Apartments")}:
          </label>
          <Select
            mode="multiple"
            allowClear
            placeholder={t("dashboard.ChangeOwnership.Select apartments")}
            value={selectedApartments}
            onChange={setSelectedApartments}
            className="w-full"
            loading={loading}
          >
            {apartments.map((apartment) => (
              <Select.Option key={apartment} value={apartment}>
                {apartment}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="block mb-2 font-semibold">
            {t("dashboard.ChangeOwnership.Parkings")}:
          </label>
          <Select
            mode="multiple"
            allowClear
            placeholder={t("dashboard.ChangeOwnership.Select parkings")}
            value={selectedParkings}
            onChange={setSelectedParkings}
            className="w-full"
            loading={loading}
          >
            {parkings.map((parking) => (
              <Select.Option key={parking} value={parking}>
                {parking}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block mb-2 font-semibold">
            {t("dashboard.ChangeOwnership.Owner")}:
          </label>
          <Select
            placeholder={t("dashboard.ChangeOwnership.Select owner")}
            value={selectedOwner}
            onChange={setSelectedOwner}
            className="w-full"
            loading={loading}
            showSearch
            filterOption={(input, option) => {
              const children = option?.children;
              const label = typeof children === 'string' 
                ? children 
                : Array.isArray(children) 
                  ? children.join('') 
                  : String(children || '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {owners.map((owner) => (
              <Select.Option key={owner.OwnerID} value={owner.OwnerID}>
                {owner.FirstName} {owner.LastName} ({owner.OwnerID})
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="block mb-2 font-semibold">
            {t("dashboard.ChangeOwnership.Change From")}:
          </label>
          <DatePicker
            value={changeFrom}
            onChange={(date) => setChangeFrom(date)}
            className="w-full"
            format="YYYY-MM-DD"
            placeholder={t("dashboard.ChangeOwnership.Select a date")}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="primary"
          onClick={handleUpdate}
          loading={submitting}
          className="btn-yellow hvr-float-shadow h-10 w-32"
        >
          {t("dashboard.ChangeOwnership.Update")}
        </Button>
      </div>
    </div>
  );
};

export default ChangeOwnership;

